import { VadParams } from "./protocol"

// Recorded on every session so that rows drawn under different designs can
// be told apart once the design changes. Like SCHEMA_VERSION, the numbering
// restarts with this codebase.
export const SAMPLER_VERSION = 1

// The negative threshold is tied to the positive one at the library's
// historical offset rather than sampled independently, which keeps the
// search space at the three dimensions model_spec.md is calibrating. The
// bottom of the positive range keeps the negative threshold well clear of
// silero's 0.01 clamp floor. Varying the gap is deliberately deferred.
const NEGATIVE_GAP = 0.15

// preSpeechPadMs is not sampled either. It is a safety margin over the
// model's onset latency rather than something a listener can feel in this
// comparison — though it does enter the emitted set E, so it is held fixed
// rather than ignored.
const PRE_SPEECH_PAD_MS = 800

// The threshold is sampled uniformly; the time parameters log-uniformly,
// because duration perception is multiplicative — 350ms against 700ms of
// redemption is a large difference while 2300ms against 2650ms is nothing,
// and log-uniform spends draws accordingly.
const POSITIVE_RANGE: [number, number] = [0.25, 0.8]
const REDEMPTION_RANGE: [number, number] = [200, 3000]
const MIN_SPEECH_RANGE: [number, number] = [50, 1200]

const uniform = (lo: number, hi: number) => lo + Math.random() * (hi - lo)
const logUniform = (lo: number, hi: number) =>
  Math.exp(uniform(Math.log(lo), Math.log(hi)))
const round10 = (x: number) => Math.round(x / 10) * 10
const round2dp = (x: number) => Math.round(x * 100) / 100

function drawParams(): VadParams {
  const positiveSpeechThreshold = round2dp(uniform(...POSITIVE_RANGE))
  return {
    positiveSpeechThreshold,
    negativeSpeechThreshold: round2dp(positiveSpeechThreshold - NEGATIVE_GAP),
    redemptionMs: round10(logUniform(...REDEMPTION_RANGE)),
    preSpeechPadMs: PRE_SPEECH_PAD_MS,
    minSpeechMs: round10(logUniform(...MIN_SPEECH_RANGE)),
  }
}

// Two settings are only worth comparing if a listener could plausibly tell
// them apart, so a pair that is within a rough perceptual epsilon on every
// dimension at once is redrawn. With ranges this wide that fires on only a
// few percent of draws.
function perceptiblyDifferent(a: VadParams, b: VadParams): boolean {
  const ratio = (x: number, y: number) => Math.max(x, y) / Math.min(x, y)
  return (
    Math.abs(a.positiveSpeechThreshold - b.positiveSpeechThreshold) >= 0.08 ||
    ratio(a.redemptionMs, b.redemptionMs) >= 1.4 ||
    ratio(a.minSpeechMs, b.minSpeechMs) >= 1.75
  )
}

/**
 * Draws the two parameter sets for a session, in display order. The arms are
 * exchangeable, which is what makes position bias measurable afterwards as
 * Alternative 1's overall win rate.
 */
export function drawArms(): [VadParams, VadParams] {
  const first = drawParams()
  for (;;) {
    const second = drawParams()
    if (perceptiblyDifferent(first, second)) return [first, second]
  }
}
